import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "add-counterparty-modal" }
const _hoisted_2 = { class: "add-counterparty-modal__search" }
const _hoisted_3 = {
  key: 0,
  class: "add-counterparty-modal__close"
}
const _hoisted_4 = {
  key: 0,
  class: "add-counterparty-modal__hint"
}
const _hoisted_5 = {
  key: 0,
  class: "add-counterparty-modal__hint"
}
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { class: "add-counterparty-modal__found" }
const _hoisted_8 = { class: "add-counterparty-modal__body-item-text" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "add-counterparty-modal__search" }
const _hoisted_11 = {
  key: 0,
  class: "add-counterparty-modal__close"
}
const _hoisted_12 = { class: "add-counterparty-modal__form" }
const _hoisted_13 = {
  key: 0,
  class: "add-counterparty-modal__error"
}
const _hoisted_14 = { class: "add-counterparty-modal__actions" }

import {
	computed,
	nextTick,
	onMounted,
	reactive,
	ref,
	watch
} from "vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppSelect, { SelectItem, SelectModelValue } from "@/components/ui-kit/select/AppSelect.vue";
import AppButton from "@/components/ui-kit/button/AppButton.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import { clientPageStore } from "@/store/client-page";
import { commonRequestCreds, financesStore } from "@/store/finances";
import {
	CounterpartyAddContainer,
	CounterpartySearched
} from "@/models/interfaces/finances/counterparty-models";
import { debounce } from "lodash";


export default /*@__PURE__*/_defineComponent({
  __name: 'ClientAddCounterpartyDropdown',
  props: {
    clientId: {},
    addedCounterparties: {},
    showCloseIcon: { type: Boolean }
  },
  emits: ["close", "mode-change"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

// ИНН — 10 или 12 цифр
const INN_REGEX = /^(\d{10}|\d{12})$/

const store = clientPageStore()
const finances = financesStore()

const $input = ref<typeof AppInput | null>(null)
const $body = ref<HTMLDivElement | null>(null)

const mode = ref<'search' | 'create'>('search')
const isLoading = ref(false)
const isSaving = ref(false)
const errorText = ref('')

const filter = ref('')
const counterparties = ref<CounterpartySearched[]>([])

const detachedHere = ref(new Set<number>())

const instances = ref<SelectItem[]>([])
const innTaken = ref(false)

const emptyForm = (): CounterpartyAddContainer => ({
	Name: '',
	TaxpayerNumber: '',
	ReasonCode: '',
	PersonalAccount: '',
	BankAccount: '',
	BankName: '',
	BankIdCode: '',
	CorrespondentBankAccount: '',
	LegalAddress: '',
	LegalForm: '',
	Note: '',
	CounterTaxpayerNumber: [10, 12],
	InstanceName1C: null,
})

const form = reactive<CounterpartyAddContainer>(emptyForm())

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

const addedIds = computed(() => new Set((props.addedCounterparties || []).map(c => c.Id)))

const sortedCounterparties = computed<CounterpartySearched[]>(() => {
	const list = counterparties.value || []
	const added = list.filter(c => addedIds.value.has(c.Id))
	const rest = list.filter(c => !addedIds.value.has(c.Id))
	return [...added, ...rest]
})

const counterpartyLabel = (cp: CounterpartySearched) => {
	const parts = [cp.Name]
	if (cp.TaxpayerNumber) parts.push(`ИНН ${cp.TaxpayerNumber}`)
	if (cp.Instance1C) parts.push(`[${cp.Instance1C}]`)
	return parts.join(' · ')
}

// Привязан к чужому клиенту => взять его нельзя.
const isRowDisabled = (cp: CounterpartySearched) =>
	cp.AttachedToClient && !addedIds.value.has(cp.Id) && !detachedHere.value.has(cp.Id)

const trimmedFilter = computed(() => filter.value.trim())

// Введённая строка — валидный ИНН (10 или 12 цифр).
const isFilterInn = computed(() => INN_REGEX.test(trimmedFilter.value))

const isInnValid = computed(() => INN_REGEX.test(form.TaxpayerNumber.trim()))

const innErrorHint = computed(() => innTaken.value
	? 'ИНН уже используется'
	: 'ИНН должен содержать 10 или 12 цифр')

const canSubmit = computed(() =>
	isInnValid.value && !!form.Name.trim() && form.InstanceName1C != null && !innTaken.value && !isSaving.value)

const onFetch = async () => {
	try {
		isLoading.value = true
		const result = await finances.counterpartyGetFilteredList({
			Filter: filter.value,
			ForBills: false,
		})
		counterparties.value = result?.List ?? []
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onSearch = async () => {
	$body.value?.scrollTo({ top: 0 })
	await onFetch()
}

const debouncedSearch = debounce(onSearch, 500)

const onAttach = async (cp: CounterpartySearched) => {
	try {
		isLoading.value = true
		await store.attachCounterparty({ Id: cp.Id, IsDraft: cp.IsDraft })
		detachedHere.value.delete(cp.Id)
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onDetach = async (cp: CounterpartySearched) => {
	try {
		isLoading.value = true
		await store.detachCounterparty(cp.Id)
		detachedHere.value.add(cp.Id)
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onToggle = async (checked: boolean, cp: CounterpartySearched) => {
	if (checked) {
		await onAttach(cp)
	} else {
		await onDetach(cp)
	}
}

const onToggleFromName = async (cp: CounterpartySearched) => {
	await onToggle(!addedIds.value.has(cp.Id), cp)
}

const onInstanceChange = (value: SelectModelValue) => {
	form.InstanceName1C = value == null ? null : (value as string)
}

// Авто-выбор «По умолчанию» за пользователя (опция с id === '').
const applyDefaultInstance = () => {
	if (form.InstanceName1C != null) {
		return
	}
	if (instances.value.some(i => i.id === '')) {
		form.InstanceName1C = ''
	}
}

const resetForm = () => {
	Object.assign(form, emptyForm())
	innTaken.value = false
	errorText.value = ''
}

const openCreateForm = () => {
	resetForm()
	form.TaxpayerNumber = trimmedFilter.value
	mode.value = 'create'
	applyDefaultInstance()
}

const backToSearch = () => {
	resetForm()
	mode.value = 'search'
}

const checkInn = debounce(async () => {
	if (!isInnValid.value) {
		innTaken.value = false
		return
	}
	try {
		const { response_ok } = await finances.checkNewTaxpayerNumber({
			Value: form.TaxpayerNumber.trim(),
			...commonRequestCreds(),
		})
		innTaken.value = !response_ok
	} catch (e) {
		console.error(e)
	}
}, 500)

const onSubmit = async () => {
	if (!canSubmit.value) return
	try {
		isSaving.value = true
		errorText.value = ''
		await store.createCounterparty({
			...form,
			Name: form.Name.trim(),
			TaxpayerNumber: form.TaxpayerNumber.trim(),
		})
		emit('close')
	} catch (e) {
		errorText.value = 'Не удалось создать контрагента'
		console.error(e)
	} finally {
		isSaving.value = false
	}
}

const loadInstances = async () => {
	try {
		const result: any = await finances.getSynchronizationInstances()
		// Эндпоинт отдаёт голый массив имён экземпляров, где null — «по умолчанию».
		const rawList: (string | null)[] = Array.isArray(result) ? result : (result?.List ?? [])
		instances.value = rawList.map((name) => ({
			id: name ?? '',
			label: name || 'По умолчанию',
		}))

		if (mode.value === 'create') {
			applyDefaultInstance()
		}
	} catch (e) {
		console.error(e)
	}
}

watch(filter, (newVal, oldVal) => {
	if (newVal === oldVal) {
		return
	}

	if (!newVal.trim()) {
		debouncedSearch.cancel()
		isLoading.value = false
		counterparties.value = []
		return
	}

	// Оверлей включаем сразу, чтобы во время дебаунса не мигало «КА не найден».
	isLoading.value = true
	debouncedSearch()
})

watch(() => form.TaxpayerNumber, () => {
	innTaken.value = false
	checkInn()
})

watch(mode, (value) => emit('mode-change', value))

onMounted(async () => {
	try {
		// Список грузим только при вводе — при пустом поле показываем подсказку.
		await loadInstances()
		await nextTick(() => $input.value?.focusInput())
	} catch (e) {
		console.error(e)
	}
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (isLoading.value || isSaving.value)
      ? (_openBlock(), _createBlock(AppLoader, {
          key: 0,
          style: _normalizeStyle(loaderStyle.value)
        }, null, 8, ["style"]))
      : _createCommentVNode("", true),
    (mode.value === 'search')
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(AppInput, {
              ref_key: "$input",
              ref: $input,
              modelValue: filter.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filter).value = $event)),
              class: "add-counterparty-modal__search-input",
              placeholder: "Название или ИНН",
              "show-search-button": ""
            }, null, 8, ["modelValue"]),
            (props.showCloseIcon)
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createVNode(AppIcon, {
                    name: "close",
                    width: "20px",
                    color: "#D2D2D2",
                    "hover-color": "#499259",
                    cursor: "pointer",
                    title: "Закрыть",
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('close')))
                  })
                ]))
              : _createCommentVNode("", true)
          ]),
          (!trimmedFilter.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, " Для поиска КА начните вводить данные в поле выше "))
            : (!isLoading.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  (!sortedCounterparties.value.length)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                        _cache[14] || (_cache[14] = _createElementVNode("div", null, "КА не найден", -1)),
                        (isFilterInn.value)
                          ? (_openBlock(), _createElementBlock("div", {
                              key: 0,
                              class: "add-counterparty-modal__hint-create",
                              onClick: openCreateForm
                            }, " Создать новый КА с ИНН " + _toDisplayString(trimmedFilter.value), 1))
                          : (_openBlock(), _createElementBlock("div", _hoisted_6, " Для создания нового КА сделайте поиск по корректному ИНН (10 или 12 цифр) "))
                      ]))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createElementVNode("div", _hoisted_7, " По запросу найдено: " + _toDisplayString(sortedCounterparties.value.length), 1),
                        _createElementVNode("div", {
                          ref_key: "$body",
                          ref: $body,
                          class: "add-counterparty-modal__body"
                        }, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedCounterparties.value, (cp) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: cp.Id,
                              class: _normalizeClass({
								'add-counterparty-modal__body-item': true,
								'add-counterparty-modal__body-item--disabled': isRowDisabled(cp)
							})
                            }, [
                              _createVNode(AppCheckbox, {
                                style: { height: '16px', marginRight: '8px' },
                                value: addedIds.value.has(cp.Id),
                                disabled: isRowDisabled(cp),
                                text: "",
                                onInput: ($event: any) => (onToggle($event, cp))
                              }, null, 8, ["value", "disabled", "onInput"]),
                              (isRowDisabled(cp))
                                ? (_openBlock(), _createBlock(AppTooltip, {
                                    key: 0,
                                    text: "Привязан к другому клиенту"
                                  }, {
                                    activator: _withCtx(() => [
                                      _createElementVNode("div", _hoisted_8, _toDisplayString(counterpartyLabel(cp)), 1)
                                    ]),
                                    _: 2
                                  }, 1024))
                                : (_openBlock(), _createElementBlock("div", {
                                    key: 1,
                                    class: "add-counterparty-modal__body-item-text",
                                    onClick: ($event: any) => (onToggleFromName(cp))
                                  }, _toDisplayString(counterpartyLabel(cp)), 9, _hoisted_9))
                            ], 2))
                          }), 128))
                        ], 512)
                      ], 64))
                ], 64))
              : _createCommentVNode("", true)
        ], 64))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
          _createElementVNode("div", _hoisted_10, [
            _cache[15] || (_cache[15] = _createElementVNode("div", { class: "add-counterparty-modal__title" }, " Новый контрагент ", -1)),
            (props.showCloseIcon)
              ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                  _createVNode(AppIcon, {
                    name: "close",
                    width: "20px",
                    color: "#D2D2D2",
                    "hover-color": "#499259",
                    cursor: "pointer",
                    title: "Закрыть",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('close')))
                  })
                ]))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", _hoisted_12, [
            _createVNode(AppInput, {
              modelValue: form.Name,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.Name) = $event)),
              label: "Название",
              required: ""
            }, null, 8, ["modelValue"]),
            _createVNode(AppInput, {
              modelValue: form.TaxpayerNumber,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.TaxpayerNumber) = $event)),
              label: "ИНН",
              required: "",
              "validation-regex": INN_REGEX,
              "show-validation-icon": "",
              error: innTaken.value,
              "error-hint-text": innErrorHint.value,
              "validation-hint-text": "ИНН корректен"
            }, null, 8, ["modelValue", "error", "error-hint-text"]),
            _createVNode(AppInput, {
              modelValue: form.ReasonCode,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((form.ReasonCode) = $event)),
              label: "КПП"
            }, null, 8, ["modelValue"]),
            _createVNode(AppInput, {
              modelValue: form.PersonalAccount,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((form.PersonalAccount) = $event)),
              label: "Лицевой счет"
            }, null, 8, ["modelValue"]),
            _createVNode(AppInput, {
              modelValue: form.BankAccount,
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((form.BankAccount) = $event)),
              label: "Расчетный счет"
            }, null, 8, ["modelValue"]),
            _createVNode(AppInput, {
              modelValue: form.BankName,
              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((form.BankName) = $event)),
              label: "Название банка"
            }, null, 8, ["modelValue"]),
            _createVNode(AppInput, {
              modelValue: form.BankIdCode,
              "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((form.BankIdCode) = $event)),
              label: "БИК"
            }, null, 8, ["modelValue"]),
            _createVNode(AppInput, {
              modelValue: form.CorrespondentBankAccount,
              "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((form.CorrespondentBankAccount) = $event)),
              label: "Корр. счет"
            }, null, 8, ["modelValue"]),
            _createVNode(AppInput, {
              modelValue: form.LegalAddress,
              "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((form.LegalAddress) = $event)),
              label: "Юр. адрес"
            }, null, 8, ["modelValue"]),
            _createVNode(AppInput, {
              modelValue: form.LegalForm,
              "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((form.LegalForm) = $event)),
              label: "Правовая форма"
            }, null, 8, ["modelValue"]),
            _createVNode(AppInput, {
              modelValue: form.Note,
              "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((form.Note) = $event)),
              label: "Комментарий"
            }, null, 8, ["modelValue"]),
            _createVNode(AppSelect, {
              items: instances.value,
              "model-value": form.InstanceName1C,
              disabled: !instances.value.length,
              placeholder: instances.value.length ? 'Экземпляр 1С' : 'Экземпляры 1С недоступны',
              "floating-label": "",
              required: "",
              searchable: "",
              "onUpdate:modelValue": onInstanceChange
            }, null, 8, ["items", "model-value", "disabled", "placeholder"]),
            (errorText.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_13, _toDisplayString(errorText.value), 1))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", _hoisted_14, [
            _createElementVNode("button", {
              type: "button",
              class: "add-counterparty-modal__actions-button add-counterparty-modal__back",
              onClick: backToSearch
            }, [
              _createVNode(AppIcon, {
                name: "chevron-left",
                width: "16px",
                color: "#979797"
              }),
              _cache[16] || (_cache[16] = _createTextVNode(" Назад "))
            ]),
            _createVNode(AppButton, {
              class: "add-counterparty-modal__actions-button",
              label: "Привязать КА",
              disabled: !canSubmit.value,
              onClick: onSubmit
            }, null, 8, ["disabled"])
          ])
        ], 64))
  ]))
}
}

})